import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaBox, FaShippingFast, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaFileInvoice, FaTimes, FaSyncAlt } from 'react-icons/fa';
import { MdPayment, MdInfo } from 'react-icons/md';
import '../../styles/OrderConfirmation.css';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { createPaymongoCheckoutSession } from '../../utils/paymongo';

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [statusBanner, setStatusBanner] = useState(null);

  const searchParams = new URLSearchParams(location.search);
  const paymentQuery = searchParams.get('payment');

  const refreshPaymentStatus = useCallback(async () => {
    if (!user || !orderId) return;

    setCheckingStatus(true);
    try {
      const token = await user.getIdToken();
      const response = await api.get(`/payments/status/${orderId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data) {
        setOrder(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            payment: {
              ...(prev.payment || {}),
              status: response.data.paymentStatus || prev.payment?.status
            },
            status: response.data.orderStatus || prev.status
          };
        });
      }
    } catch (err) {
      console.error('Error refreshing payment status:', err);
      toast.error('Unable to refresh payment status right now.');
    } finally {
      setCheckingStatus(false);
    }
  }, [orderId, user]);

  useEffect(() => {
    if (!paymentQuery) return;

    if (paymentQuery === 'success') {
      setStatusBanner({
        type: 'success',
        title: 'Payment successful',
        message: 'Thanks for completing your payment. We are preparing your order!'
      });
      refreshPaymentStatus();
    } else if (paymentQuery === 'cancelled') {
      setStatusBanner({
        type: 'warning',
        title: 'Payment was cancelled',
        message: 'You can restart the PayMongo checkout any time using the button below.'
      });
    } else if (paymentQuery === 'failed') {
      setStatusBanner({
        type: 'warning',
        title: 'Payment failed',
        message: 'Please try again or use a different payment method through PayMongo.'
      });
    }
  }, [paymentQuery, refreshPaymentStatus]);

  // Get order details from location state or fetch from API
  useEffect(() => {
    const fetchOrderDetails = async () => {
      setLoading(true);
      try {
        if (location.state?.order) {
          setOrder(location.state.order);
          setLoading(false);
          return;
        }

        if (!orderId || !user) {
          setError('Order information not found');
          setLoading(false);
          return;
        }
        
        const token = await user.getIdToken();
        const response = await api.get(`/orders/${orderId}`, {
              headers: { 
            Authorization: `Bearer ${token}`
          }
        });

        if (response.data) {
          setOrder(response.data);
          } else {
          setError('Order not found');
        }
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError('Failed to load order details');
        toast.error('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId, user, location.state]);

  const handlePayNow = async () => {
    if (!user) {
      toast.error('Please sign in to continue to payment.');
      return;
    }

    if (!order) return;

    setCreatingCheckout(true);
    try {
      const token = await user.getIdToken();
      const checkoutUrl = await createPaymongoCheckoutSession({
        orderId: order._id,
        amount: order.summary?.total,
        description: `Order ${order.orderNumber || order._id}`,
        remarks: `Beauty Lab Cosmetic order for ${order.customerName || ''}`,
        token,
        metadata: {
          userId: user.uid,
          customerName: order.customerName || `${order.shipping?.firstName || ''} ${order.shipping?.lastName || ''}`.trim(),
          customerEmail: order.shipping?.email,
          orderNumber: order.orderNumber
        }
      });

      toast.success('Redirecting you to PayMongo...');
      window.location.assign(checkoutUrl);
    } catch (err) {
      console.error('Failed to initiate PayMongo checkout:', err);
      toast.error(err?.response?.data?.error || 'Unable to start PayMongo checkout right now. Please try again.');
    } finally {
      setCreatingCheckout(false);
    }
  };

  if (loading) {
    return (
      <div className="order-confirmation-page">
        <div className="container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading order details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-confirmation-page">
        <div className="container">
          <div className="error-container">
            <h2>Order Not Found</h2>
            <p>{error || 'Unable to retrieve order information'}</p>
            <Link to="/products" className="primary-button">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const paymentStatus = (order.payment?.status || 'pending').toLowerCase();
  const paymentStatusCopy = {
    paid: {
      label: 'Payment Successful',
      badgeClass: 'paid',
      description: 'Your payment has been confirmed. We are preparing your order for dispatch.'
    },
    pending: {
      label: 'Awaiting Payment',
      badgeClass: 'pending',
      description: 'Please complete your PayMongo payment to keep your order moving.'
    },
    failed: {
      label: 'Payment Failed',
      badgeClass: 'failed',
      description: 'The last payment attempt was unsuccessful. You can try again via PayMongo below.'
    }
  };

  const paymentMeta = paymentStatusCopy[paymentStatus] || paymentStatusCopy.pending;
  const showPayActions = paymentStatus !== 'paid';

  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div className="order-confirmation-page">
      <div className="container">
        {statusBanner && (
          <div className={`status-banner ${statusBanner.type}`}>
            <div className="status-banner-content">
              <MdInfo className="status-banner-icon" />
              <div>
                <p className="status-banner-title">{statusBanner.title}</p>
                <p className="status-banner-message">{statusBanner.message}</p>
              </div>
            </div>
            <button
              className="status-banner-close"
              onClick={() => setStatusBanner(null)}
              aria-label="Dismiss message"
            >
              <FaTimes />
            </button>
          </div>
        )}
        <motion.div 
          className="confirmation-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="success-icon">
            <FaCheckCircle />
          </div>
          <h1>Thank You for Your Order!</h1>
          <p className="confirmation-message">
            Your order has been received and is being processed. You will receive an email confirmation shortly.
          </p>
        </motion.div>

        <motion.div 
          className="order-info-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="order-info-header">
            <h2>Order Information</h2>
            <span className="order-status">{order.status}</span>
          </div>
          <div className="order-details-grid">
            <div className="order-detail">
              <span className="detail-label">Order ID:{order.orderNumber || order._id}</span>
            </div>
            <div className="order-detail">
              <span className="detail-label">Order Date:</span>
              <span className="detail-value">{formatDate(order.createdAt)}</span>
            </div>
            <div className="order-detail">
              <span className="detail-label">Customer Name:</span>
              <span className="detail-value">{order.customerName}</span>
            </div>
            <div className="order-detail">
              <span className="detail-label">Email:</span>
              <span className="detail-value">{order.shipping?.email}</span>
            </div>
          </div>
        </motion.div>

        <motion.div 
          className="payment-info-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div className="card-header">
            <MdPayment className="card-icon" />
            <h2>Payment Information</h2>
          </div>
          <div className="payment-status">
            <div className={`status-badge ${paymentMeta.badgeClass}`}>{paymentMeta.label}</div>
          </div>
          <p className="payment-note">{paymentMeta.description}</p>
          {showPayActions && (
            <div className="payment-actions">
              <button
                className="primary-button paymongo-button"
                onClick={handlePayNow}
                disabled={creatingCheckout}
              >
                {creatingCheckout ? 'Redirecting...' : 'Pay Now via PayMongo'}
              </button>
              <button
                className="secondary-button refresh-status-button"
                onClick={refreshPaymentStatus}
                disabled={checkingStatus}
              >
                <FaSyncAlt className="mr-2" />
                {checkingStatus ? 'Checking...' : 'Refresh Status'}
              </button>
            </div>
          )}
          <div className="payment-amount">
            <div>
              <span className="amount-label">Total Amount Due</span>
              <span className="amount">₱{order.summary?.total.toLocaleString('en-PH', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}</span>
            </div>
            <div className="text-right">
              <span className="amount-label">Payment Method</span>
              <span className="payment-method-value">PayMongo Checkout</span>
            </div>
          </div>
          <p className="payment-footnote">Payments are securely processed by PayMongo. Once completed, your order status updates automatically.</p>
        </motion.div>

        <div className="two-column-grid">
          <motion.div 
            className="shipping-info-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="card-header">
              <FaShippingFast className="card-icon" />
              <h2>Shipping Information</h2>
            </div>
            <div className="shipping-details">
              <div className="shipping-detail">
                <FaMapMarkerAlt className="detail-icon" />
                <div>
                  <h4>Delivery Address</h4>
                  <p>{order.shipping?.fullAddress}</p>
                </div>
              </div>
              <div className="shipping-detail">
                <FaPhoneAlt className="detail-icon" />
                <div>
                  <h4>Contact Number</h4>
                  <p>{order.shipping?.phone}</p>
                </div>
              </div>
              <div className="shipping-detail">
                <FaEnvelope className="detail-icon" />
                <div>
                  <h4>Email</h4>
                  <p>{order.shipping?.email}</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="order-summary-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div className="card-header">
              <FaFileInvoice className="card-icon" />
              <h2>Order Summary</h2>
            </div>
            <div className="summary-details">
              <div className="summary-row">
                <span>Subtotal</span>
                <span>₱{order.summary?.subtotal.toLocaleString('en-PH', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}</span>
              </div>
              <div className="summary-row">
                <span>Tax (12%)</span>
                <span>₱{order.summary?.tax.toLocaleString('en-PH', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}</span>
              </div>
              <div className="summary-row">
                <span>Shipping</span>
                <span>₱{order.summary?.shipping.toLocaleString('en-PH', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}</span>
              </div>
              {order.summary?.rewardDiscount > 0 && (
                <div className="summary-row discount">
                  <span>Reward Discount</span>
                  <span>-₱{order.summary.rewardDiscount.toLocaleString('en-PH', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}</span>
                </div>
              )}
              <div className="summary-total">
                <span>Total</span>
                <span>₱{order.summary?.total.toLocaleString('en-PH', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}</span>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div 
          className="order-items-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <div className="card-header">
            <FaBox className="card-icon" />
            <h2>Order Items</h2>
            <span className="item-count">{order.items?.length || 0} items</span>
          </div>
          <div className="order-items-list">
            {order.items?.map((item, index) => (
              <div className="order-item" key={index}>
                <img 
                  src={item.image || 'https://via.placeholder.com/100'} 
                  alt={item.name} 
                  className="item-image"
                />
                <div className="item-details">
                  <h4 className="item-name">{item.name}</h4>
                  {item.variationDisplay && (
                    <p className="item-variation">Variation: {item.variationDisplay}</p>
                  )}
                  <div className="item-meta">
                    <span className="item-quantity">Qty: {item.quantity}</span>
                    <span className="item-price">₱{Number(item.price).toLocaleString('en-PH', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}</span>
                  </div>
                </div>
                <div className="item-subtotal">
                  <span>₱{Number(item.subtotal || (item.price * item.quantity)).toLocaleString('en-PH', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div 
          className="action-buttons"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <Link to="/products" className="primary-button">
            Continue Shopping
          </Link>
          {user ? (
            <Link to="/order-history" className="secondary-button">
              View All Orders
            </Link>
          ) : (
            <Link to="/login" className="secondary-button">
              Sign In to View Your Orders
            </Link>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default OrderConfirmation; 